import { useState, useEffect, useCallback } from "react";
import { useOperationRegion } from "./useOperationRegion";

/**
 * localStorage 存储键名
 */
const CONSENT_STORAGE_KEY = "cookie_consent_preferences";

/**
 * 同意数据版本号（用于将来升级）
 */
const CONSENT_VERSION = 1;

/**
 * Cookie 类别
 */
export type CookieCategory =
  | "necessary"
  | "performance"
  | "functional"
  | "targeting";

/**
 * Cookie 偏好设置
 */
export interface CookiePreferences {
  /** 必要 Cookie - 始终为 true，不可更改 */
  necessary: boolean;
  /** 性能分析 Cookie - 默认 false */
  performance: boolean;
  /** 功能性 Cookie - 默认 false */
  functional: boolean;
  /** 定向广告 Cookie - 默认 false */
  targeting: boolean;
}

/**
 * 存储的同意状态数据结构
 */
interface StoredConsentState {
  /** Cookie 偏好设置 */
  preferences: CookiePreferences;
  /** 同意时间戳（Unix 毫秒） */
  timestamp: number;
  /** 版本号 */
  version: number;
}

/**
 * 默认偏好设置（仅必要 Cookie 开启）
 */
const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  performance: false,
  functional: false,
  targeting: false,
};

/**
 * 全部接受的偏好设置
 */
const ALL_ACCEPTED_PREFERENCES: CookiePreferences = {
  necessary: true,
  performance: true,
  functional: true,
  targeting: true,
};

/**
 * Hook 返回值类型
 */
export interface UseCookieConsentReturn {
  /** Cookie 偏好设置 */
  preferences: CookiePreferences;
  /** 是否已做出选择 */
  hasConsented: boolean;
  /** 同意时间戳 */
  consentedAt: number | null;
  /** 是否应该显示横幅 */
  shouldShowBanner: boolean;
  /** 是否显示偏好设置弹窗 */
  showPreferencesModal: boolean;
  /** 加载状态 */
  isLoading: boolean;
  /** 接受所有 Cookie */
  acceptAll: () => void;
  /** 拒绝非必要 Cookie */
  rejectNonEssential: () => void;
  /** 保存自定义偏好 */
  savePreferences: (prefs: Partial<CookiePreferences>) => void;
  /** 打开偏好设置弹窗 */
  openPreferencesModal: () => void;
  /** 关闭偏好设置弹窗 */
  closePreferencesModal: () => void;
}

/**
 * 安全读取 localStorage 中的同意状态
 */
function safeGetConsentState(): StoredConsentState | null {
  try {
    const data = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!data) return null;

    const parsed = JSON.parse(data);
    // 验证数据结构
    if (
      !parsed.preferences ||
      typeof parsed.preferences.necessary !== "boolean" ||
      typeof parsed.timestamp !== "number"
    ) {
      // 数据损坏，清除
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    return parsed as StoredConsentState;
  } catch (e) {
    console.warn("Failed to read cookie consent state:", e);
    return null;
  }
}

/**
 * 安全写入 localStorage
 */
function safeSetConsentState(data: StoredConsentState): boolean {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (e) {
    console.warn("Failed to save cookie consent state:", e);
    return false;
  }
}

/**
 * Cookie 同意状态管理 Hook (GDPR 合规版本)
 *
 * 用于管理用户的 Cookie 同意状态，支持四类 Cookie 的精细控制。
 * 仅在国际版模式下显示横幅。
 *
 * 特性：
 * 1. 支持 Accept All / Reject Non-Essential / 自定义偏好
 * 2. 从 localStorage 读取和存储偏好状态
 * 3. 结合运营区域判断是否显示横幅
 * 4. 提供偏好设置弹窗的开关控制
 *
 * @returns {UseCookieConsentReturn} 同意状态和操作方法
 */
export function useCookieConsent(): UseCookieConsentReturn {
  const [preferences, setPreferences] =
    useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [hasConsented, setHasConsented] = useState(false);
  const [consentedAt, setConsentedAt] = useState<number | null>(null);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { isInternational, loading: regionLoading } = useOperationRegion();

  // 初始化时读取 localStorage
  useEffect(() => {
    const storedState = safeGetConsentState();
    if (storedState) {
      setPreferences({
        ...DEFAULT_PREFERENCES,
        ...storedState.preferences,
        necessary: true, // 确保必要 Cookie 始终为 true
      });
      setHasConsented(true);
      setConsentedAt(storedState.timestamp);
    }
    setIsLoading(false);
  }, []);

  /**
   * 保存偏好设置到 localStorage
   */
  const saveToStorage = useCallback((prefs: CookiePreferences) => {
    const timestamp = Date.now();
    const consentData: StoredConsentState = {
      preferences: {
        ...prefs,
        necessary: true, // 确保必要 Cookie 始终为 true
      },
      timestamp,
      version: CONSENT_VERSION,
    };

    if (safeSetConsentState(consentData)) {
      setPreferences(consentData.preferences);
      setHasConsented(true);
      setConsentedAt(timestamp);
    }
  }, []);

  /**
   * 接受所有 Cookie
   */
  const acceptAll = useCallback(() => {
    saveToStorage(ALL_ACCEPTED_PREFERENCES);
    setShowPreferencesModal(false);
  }, [saveToStorage]);

  /**
   * 拒绝非必要 Cookie（仅保留必要 Cookie）
   */
  const rejectNonEssential = useCallback(() => {
    saveToStorage(DEFAULT_PREFERENCES);
    setShowPreferencesModal(false);
  }, [saveToStorage]);

  /**
   * 保存自定义偏好设置
   */
  const savePreferences = useCallback(
    (prefs: Partial<CookiePreferences>) => {
      const newPreferences: CookiePreferences = {
        ...preferences,
        ...prefs,
        necessary: true, // 确保必要 Cookie 始终为 true
      };
      saveToStorage(newPreferences);
      setShowPreferencesModal(false);
    },
    [preferences, saveToStorage]
  );

  /**
   * 打开偏好设置弹窗
   */
  const openPreferencesModal = useCallback(() => {
    setShowPreferencesModal(true);
  }, []);

  /**
   * 关闭偏好设置弹窗
   */
  const closePreferencesModal = useCallback(() => {
    setShowPreferencesModal(false);
  }, []);

  // 计算是否应该显示横幅
  // 条件：国际版 + 未同意 + 加载完成
  const shouldShowBanner =
    !isLoading && !regionLoading && isInternational && !hasConsented;

  return {
    preferences,
    hasConsented,
    consentedAt,
    shouldShowBanner,
    showPreferencesModal,
    isLoading: isLoading || regionLoading,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferencesModal,
    closePreferencesModal,
  };
}

export default useCookieConsent;
